<template>
  <div :class="{ 'sidebar-visible': sidebarVisible }">
    <base-header class="pb-6" />
    <b-container fluid class="mt--6 max-width">
      <b-card no-body>
        <!-- Card Header -->
        <card-header title="Devices">
          <div class="d-flex justify-content-end align-items-center">
            <b-button
              @click="toggleSidebar"
              variant="outline-secondary"
              size="sm"
              class="mr-2"
              aria-label="Toggle Filters Sidebar"
              :aria-expanded="sidebarVisible"
            >
              <i class="fas fa-filter"></i>
              Filter
              <i
                :class="sidebarVisible ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                class="transition-icon"
              ></i>
            </b-button>
            <base-button type="outline-primary" size="sm" @click="onNew('multiple')"
              >Add Multiple Devices</base-button
            >
            <base-button size="sm" @click="onNew('single')">Add Device</base-button>
            <el-dropdown trigger="click">
              <base-button size="sm" type="info" style="font-size: 12px">
                Multi-Device Action
              </base-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item>
                  <base-button
                    size="sm"
                    type="info"
                    @click="MultiDeviceAction(persistentSelectedIds, 'refresh')"
                    icon
                  >
                    <i class="fas fa-sync-alt mr-1"></i> Refresh State ({{ persistentSelectedIds.size }})
                  </base-button>
                </el-dropdown-item>
           
                <el-dropdown-item>
                  <base-button
                    size="sm"
                    type="secondary"
                    @click="sendMultiDeviceDFU(persistentSelectedIds)"
                    icon
                  >
                    <i class="fas fa-cloud-download-alt mr-1"></i> Update Firmware ({{ persistentSelectedIds.size }})
                  </base-button>
                </el-dropdown-item>
     
                <el-dropdown-item>
                  <base-button
                    size="sm"
                    type="warning"
                    @click="MultiDeviceAction(persistentSelectedIds, 'vibrate')"
                    icon
                  >
                    <i class="fas fa-bell mr-1"></i> Vibrate ({{ persistentSelectedIds.size }})
                  </base-button>
                </el-dropdown-item>
                <el-dropdown-item>
                  <base-button
                    size="sm"
                    type="danger"
                    @click="MultiDeviceAction(persistentSelectedIds, 'reboot')"
                    icon
                  >
                    <i class="fas fa-bell mr-1"></i> Reboot ({{ persistentSelectedIds.size }})
                  </base-button>
                </el-dropdown-item>
                <el-dropdown-item divided>
                  <base-button
                    size="sm"
                    type="info"
                    @click="AllDeviceAction('refresh')"
                    icon
                  >
                    <i class="fas fa-sync-alt mr-1"></i> Refresh State (All)
                  </base-button>
                </el-dropdown-item>
                <el-dropdown-item divided>
                  <base-button
                    size="sm"
                    type="danger"
                    @click="deleteAllDevices"
                    icon
                    disabled
                  >
                    <i class="fas fa-trash-alt mr-1"></i> Delete All Devices
                  </base-button>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <base-button size="sm" type="primary" @click="openScheduleCreateModal">
              <i class="fas fa-calendar-plus mr-1"></i>New Schedule
            </base-button>
          </div>
        </card-header>

        <transition name="slide-fade">
          <filter-sidebar
            v-if="sidebarVisible"
            ref="filterSidebar"
            :title="'Device Filters'"
            :filters-config="deviceFiltersConfig"
            :visible="sidebarVisible"
            v-model="activeFilters"
            @filter-change="handleFilterChange"
            @filter-reset="handleFilterReset"
          />
        </transition>
        <schedule-badge @click-schedules="$router.push({ name: 'ScheduleList' })" />

        <el-table
          header-row-class-name="thead-light"
          :header-cell-class-name="() => 'fixed-header'"
          :data="devicesWithSchedule"
          @selection-change="handleSelectionChange"
          ref="devicesTable"
          :row-key="'name'"
          :row-class-name="getRowClassName"
          class="table-with-transition"
        >
          <template #empty>
            <span v-if="loading">Loading Devices <i class="fas fa-sync-alt fa-spin mr-1"></i></span>
            <span v-else>No Devices Found</span>
          </template>
          <el-table-column type="selection" width="55" :reserve-selection="true" />
          <el-table-column header-class-name="fixed-header" class-name="font-weight-bold" label="Device ID" sortable prop="name" />
          <el-table-column label="Groups" sortable prop="group" />
          <el-table-column label="Version" prop="fw_version">
            <template v-slot="{ row }">{{ row.fw_version }}</template>
          </el-table-column>
          <el-table-column label="Status">
            <template v-slot="{ row }">
              <span :class="getDeviceStatusClass(row.state.current)" class="font-weight-bold">
                {{ row.state.current }}
              </span>
            </template>
          </el-table-column>

                    <el-table-column label="Schedule" width="110">
            <template #default="{ row }">
              <span v-if="row._scheduleEntry" :title="row._scheduleEntry.scheduleName">
                <i
                  :class="scheduleEntryIcon(row._scheduleEntry.status)"
                  :style="{ color: scheduleEntryColor(row._scheduleEntry.status) }"
                ></i>
                <small class="ml-1">{{ row._scheduleEntry.status }}</small>
              </span>
            </template>
          </el-table-column>
          <el-table-column label="Created Date">
            <template v-slot="{ row }">{{ formatDate(row.created_at) }}</template>
          </el-table-column>
          <el-table-column label="Last Updated">
            <template v-slot="{ row }">
              <span :class="DeviceUnresponsive(row.updated_at) ? 'text-danger' : ''">
                {{ formatTimeAgo(row.updated_at) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="Battery">
            <template slot-scope="{ row }">
              <div class="battery-cell">
                <span v-html="batteryFormatter(row.battery)"></span>
                <i v-if="row.is_charging" class="ml-1 fas fa-bolt charging-icon"></i>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="Actions" width="320px">
            <template #default="{ row }">
             <base-button
                 size="sm"
                :type="(row.current_provision && !isProvisionStale(row)) ? 'info' : 'primary'"
                @click="handleProvisionClick(row)"
                icon
              >
                <span v-if="row.current_provision && !isProvisionStale(row)">
                  <i class="fas fa-sync-alt fa-spin mr-1"></i> Provisioning...
                </span>
                <span v-else>
                  <i class="fas fa-cogs mr-1"></i> Provision
                </span>
              </base-button>
              <base-button
                :disabled="DeviceUnresponsive(row.updated_at)"
                size="sm"
                type="outline-warning"
                @click="cancelProvisioning(row.name)"
                icon
              >
                <i class="fas fa-ban mr-1"></i>
              </base-button>
              <base-button size="sm" type="danger" @click="onDelete(row)" icon>
                <i class="fas fa-trash-alt mr-1"></i>
              </base-button>
              <el-dropdown trigger="click">
                <base-button size="sm" type="info" icon>
                  <i class="fas fa-ellipsis-v"></i>
                </base-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item>
                    <base-button size="sm" type="secondary" @click="onEdit(row)" icon>
                      <i class="fas fa-edit mr-1"></i> Edit
                    </base-button>
                  </el-dropdown-item>
                  <el-dropdown-item>
  <base-button
    :disabled="DeviceUnresponsive(row.updated_at)"
    size="sm"
    type="secondary"
    @click="openFileManager(row)"
  >
    <i class="fas fa-folder-open mr-1"></i> File Manager
  </base-button>
</el-dropdown-item>
                  <el-dropdown-item divided>
                    <base-button
                      :disabled="DeviceUnresponsive(row.updated_at)"
                      size="sm"
                      type="info"
                      @click="sendMqttGeneralCommand(row, 'refresh')"
                    >
                      <i class="fas fa-sync-alt mr-1"></i> Refresh
                    </base-button>
                  </el-dropdown-item>
                  <el-dropdown-item>
                    <base-button
                      :disabled="DeviceUnresponsive(row.updated_at)"
                      size="sm"
                      type="warning"
                      @click="sendMqttGeneralCommand(row, 'vibrate')"
                    >
                      <i class="fas fa-bell mr-1"></i> Vibrate
                    </base-button>
                  </el-dropdown-item>
                  <el-dropdown-item>
                    <base-button
                      :disabled="DeviceUnresponsive(row.updated_at)"
                      size="sm"
                      type="primary"
                      @click="openFirmwareModal(row)"
                    >
                      <i class="fas fa-cloud-download-alt mr-1"></i> Update Firmware
                    </base-button>
                  </el-dropdown-item>
                  <el-dropdown-item>
                    <base-button
                      :disabled="DeviceUnresponsive(row.updated_at)"
                      size="sm"
                      type="success"
                      @click="sendMqttGeneralCommand(row, 'allocate')"
                    >
                      <i class="fas fa-link mr-1"></i> Allocate
                    </base-button>
                  </el-dropdown-item>
                  <el-dropdown-item>
                    <base-button
                      :disabled="DeviceUnresponsive(row.updated_at)"
                      size="sm"
                      type="warning"
                      @click="sendMqttGeneralCommand(row, 'unallocate')"
                    >
                      <i class="fas fa-unlink mr-1"></i> Unallocate
                    </base-button>
</el-dropdown-item>
                  <el-dropdown-item>
                    <base-button
                      :disabled="DeviceUnresponsive(row.updated_at)"
                      size="sm"
                      type="danger"
                      @click="sendMqttGeneralCommand(row, 'reboot')"
                    >
                      <i class="fas fa-power-off mr-1"></i> Reboot
                    </base-button>
                  </el-dropdown-item>
                  
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
      </b-card>
    </b-container>

    <!-- Step 1: file selection dialog -->
    <ModalProvisionFileSelect
      v-if="isFileSelectVisible"
      :device-id="currentProvisioningDevice && currentProvisioningDevice.name"
      :entity="activeEntity"
      @start-provision="onFilesSelected"
      @close-modal="closeFileSelectModal"
    />

    <!-- Step 2: provisioning progress dialog -->
    <ModalProvision
      ref="provisioningModal"
      v-if="isProvModalVisible"
      :device-id="currentProvisioningDevice && currentProvisioningDevice.name"
      :entity="activeEntity"
      @close-modal="closeProvisioningModal"
    />
        <ModalFileManager
      v-if="isFileManagerVisible"
      v-model="isFileManagerVisible"
      :device-id="fileManagerDevice?.name"
      @close="isFileManagerVisible = false"
    />

        <ModalFirmwareOTA
       v-if="isFirmwareModalVisible"
  :key="firmwareModalDevice?.name"
  :device-id="firmwareModalDevice?.name"  
      :entity="activeEntity"
      :current-fw-version="firmwareModalDevice?.fw_version"
      :device-state="firmwareModalDevice?.state?.current"
      @ota-completed="loadDevice"

      @close-modal="closeFirmwareModal"
    />
    <modal-schedule-create
  ref="scheduleCreateModal"
  @created="onLocalScheduleCreated"
  @updated="onLocalScheduleUpdated"
/>
  </div>
</template>

<script>
import { Routes } from "@/router";
import { Action } from "@/store/actions";
import FilterSidebar from "../../views/components/FilterSidebar.vue";
import { mapState, mapGetters } from "vuex";
import {
  Table,
  TableColumn,
  Dropdown,
  DropdownMenu,
  DropdownItem,
} from "element-ui";
import Swal from "sweetalert2";
import { confirmDelete } from "@/helper";
import ModalProvision from "../../views/components/ModalProvision.vue";
import ModalProvisionFileSelect from "../../views/components/ModalProvisionFileSelect.vue";
import ModalFirmwareOTA from "../../views/components/ModalFirmwareOTA.vue";
import ModalFileManager from "../../views/components/ModalFileManager.vue";
import ScheduleBadge from '@/views/components/ScheduleBadge';
import ModalScheduleCreate from '../../views/components/ModalScheduleCreate';
export default {
  name: "DeviceList",
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    ModalProvision,
    ModalProvisionFileSelect,
    FilterSidebar,
    ModalFirmwareOTA,
    ModalFileManager,
    ScheduleBadge,
      ModalScheduleCreate,

  },
  data() {
    return {
      refreshInterval: null,
      sidebarVisible: false,
      activeFilters: (() => {
        try {
          const saved = localStorage.getItem('deviceListFilters');
          return saved ? JSON.parse(saved) : { status: [], battery: [], firmwareVersion: [], dateRange: null, activeStatus: [] };
        } catch {
          return { status: [], battery: [], firmwareVersion: [], dateRange: null, activeStatus: [] };
        }
      })(),
      selectedDevices: [],
      persistentSelectedIds: new Set(),
      filterActive: false,
      showSelection: false,
      sortOrder: null,
      selectedCreatedDate: null,
      currentProvisioningDevice: null,
      isFileSelectVisible: false,
      _pendingProvision: null,
      isProvModalVisible: false,
      loading: false,
      deviceFiltersConfig: [
        {
          id: "status",
          label: "Device Status",
          type: "checkbox",
           options: [
   
  ]
        },
        {
          id: "battery",
          label: "Battery",
          type: "checkbox",
          options: [
            { value: "High", text: "High" },
            { value: "Medium", text: "Medium" },
            { value: "Low", text: "Low" },
            { value: "Charging", text: "Charging" },
          ],
        },
        {
          id: "firmwareVersion",
          label: "Firmware Version",
          type: "checkbox",
          options: [],
        },
        {
          id: "dateRange",
          label: "Date Range",
          type: "dropdown",
          options: [
            { value: "24_hours", text: "Last 24 Hours" },
            { value: "3_days", text: "Last 3 Days" },
            { value: "week", text: "Last Week" },
            { value: "month", text: "Last Month" },
            { value: "3_months", text: "Last 3 Months" },
            { value: "6_months", text: "Last 6 Months" },
            { value: "year", text: "Last Year" },
          ],
        },
          {
    id: "activeStatus",
    label: "Active Status",
    type: "checkbox",
    options: [
      { value: "active", text: "Active" },
      { value: "inactive", text: "Inactive" }
    ]
  }
      ],
       isFirmwareModalVisible: false,
    firmwareModalDevice: null,
    isFileManagerVisible: false, fileManagerDevice: null
    };
  },
  computed: {
    ...mapState({
      devices: (state) => state.device.devices,
    }),
    ...mapGetters(["activeEntity"]),


        devicesWithSchedule() {
      const summary = this.$store.getters.scheduleSummary;
      if (!summary || !summary.active_schedules) return this.filteredDevices;

      // Get the first active schedule (simplified; you may later support multiple)
      const activeSchedule = this.$store.getters.schedules?.[0];
      if (!activeSchedule) return this.filteredDevices;

      return this.filteredDevices.map(device => {
        const entry = (activeSchedule.device_entries || [])
          .find(e => e.device_id === device.name);
        return entry
          ? { ...device, _scheduleEntry: { ...entry, scheduleName: activeSchedule.name } }
          : device;
      });
    },
    
    dynamicStatusOptions() {
      const statuses = new Set(this.devices.map((device) => device.state?.current));
      return Array.from(statuses).map((status) => ({ value: status, text: status }));
    },
    filteredDevices() {
      let filtered = this.devices;
      if (this.activeFilters.status?.length) {
        filtered = filtered.filter((device) => this.activeFilters.status.includes(device.state?.current));
      }
      if (this.activeFilters.firmwareVersion?.length) {
        filtered = filtered.filter((device) => this.activeFilters.firmwareVersion.includes(device.fw_version));
      }
      if (this.activeFilters.battery?.length) {
        filtered = filtered.filter((device) => {
          if (this.activeFilters.battery.includes("High") && device.battery > 75) return true;
          if (this.activeFilters.battery.includes("Medium") && device.battery > 50 && device.battery <= 75) return true;
          if (this.activeFilters.battery.includes("Low") && device.battery <= 50) return true;
          if (this.activeFilters.battery.includes("Charging") && device.is_charging) return true;
          return false;
        });
      }
      if (this.activeFilters.dateRange) {
        const now = new Date();
        let startDate;
        switch (this.activeFilters.dateRange) {
          case "24_hours": startDate = new Date(now - 24 * 60 * 60 * 1000); break;
          case "3_days": startDate = new Date(now - 3 * 24 * 60 * 60 * 1000); break;
          case "week": startDate = new Date(now - 7 * 24 * 60 * 60 * 1000); break;
          case "month": startDate = new Date(now - 30 * 24 * 60 * 60 * 1000); break;
          case "3_months": startDate = new Date(now - 90 * 24 * 60 * 60 * 1000); break;
          case "6_months": startDate = new Date(now - 180 * 24 * 60 * 60 * 1000); break;
          case "year": startDate = new Date(now - 365 * 24 * 60 * 60 * 1000); break;
        }
        if (startDate) {
          filtered = filtered.filter((device) => new Date(device.created_at) >= startDate && new Date(device.created_at) <= now);
        }
      }
      // Active status filter
if (this.activeFilters.activeStatus && this.activeFilters.activeStatus.length) {
  filtered = filtered.filter(device => {
    const isActive = !this.DeviceUnresponsive(device.updated_at);
    const selected = this.activeFilters.activeStatus;

    // If both options are selected, show all (no filtering)
    if (selected.includes('active') && selected.includes('inactive')) {
      return true;
    }
    // If only 'active' is selected
    if (selected.includes('active')) {
      return isActive;
    }
    // If only 'inactive' is selected
    if (selected.includes('inactive')) {
      return !isActive;
    }
    return true; // no selection (should not happen because length > 0)
  });
}
      return filtered;
    },
    firmwareVersionOptions() {
      const versions = new Set(this.devices.map((device) => device.fw_version));
      return Array.from(versions);
    },
    
  },
watch: {
  activeFilters: {
    deep: true,
    handler(val) {
      localStorage.setItem('deviceListFilters', JSON.stringify(val));
    }
  },
  isFileSelectVisible(visible) {
    if (visible) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    } else if (!this.isProvModalVisible && !this.refreshInterval) {
      this.refreshInterval = setInterval(this.loadDevice, 30000);
    }
  },
  isProvModalVisible(visible) {
    if (visible) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    } else {
      if (!this.refreshInterval) {
        this.refreshInterval = setInterval(this.loadDevice, 30000);
      }
    }
  },
},
  methods: {

        scheduleEntryIcon(status) {
      const map = {
        pending:     'far fa-clock',
        ready:       'fas fa-bolt',
        in_progress: 'fas fa-circle-notch fa-spin',
        completed:   'fas fa-check-circle',
        failed:      'fas fa-times-circle',
        expired:     'fas fa-hourglass-end',
      };
      return map[status] || 'fas fa-circle';
    },
    scheduleEntryColor(status) {
      const map = {
        pending:     '#adb5bd',
        ready:       '#fb6340',
        in_progress: '#11cdef',
        completed:   '#2dce89',
        failed:      '#f5365c',
        expired:     '#f5365c',
      };
      return map[status] || '#adb5bd';
    },
    openScheduleCreateModal() {
      this.$refs.scheduleCreateModal.open();
    },
    onLocalScheduleCreated() {
      // Refresh schedule summary so the badge updates
      this.$store.dispatch('schedule/loadSummary');
      // Optionally reload devices to reflect any schedule-linked status changes
      this.loadDevice();
    },
    onLocalScheduleUpdated() {
      this.$store.dispatch('schedule/loadSummary');
      this.loadDevice();
    },
    getRowClassName({ row }) {
      return this.DeviceUnresponsive(row.updated_at) ? "warning-row" : "";
    },
    DeviceUnresponsive(datestring) {
      if (!datestring) return true;
      const twentyMinutesAgo = new Date(Date.now() - 20 * 60 * 1000);
      return new Date(datestring) < twentyMinutesAgo;
    },
    formatTimeAgo(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      const seconds = Math.floor((Date.now() - date) / 1000);
      if (seconds < 5) return "just now";
      if (seconds < 60) return `${seconds} seconds ago`;
      if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
      if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
      if (seconds < 2592000) return `${Math.floor(seconds / 604800)} weeks ago`;
      if (seconds < 31536000) return `${Math.floor(seconds / 2592000)} months ago`;
      return `${Math.floor(seconds / 31536000)} years ago`;
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    },
    batteryFormatter(battery) {
      if (battery > 75) return `<span style="color:green">${battery}%</span>`;
      if (battery > 50) return `<span style="color:orange">${battery}%</span>`;
      return `<span style="color:red">${battery}%</span>`;
    },
    getDeviceStatusClass(status) {
      switch (status) {
        case "ALLOCATED": return "text-success";
        case "PROVISIONING":
        case "DFU": return "text-primary";
        case "ERROR":
        case "DEFECT":
        case "UNAVAILABLE": return "text-danger";
        case "BOOTING":
        case "WAITING": return "text-warning";
        default: return "text-neutral";
      }
    },
    // Filter methods
    toggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible;
    },
    handleFilterChange({ filterId, value }) {
      this.activeFilters[filterId] = value;
    },
    handleFilterReset() {
      this.activeFilters = { status: [], battery: [], firmwareVersion: [], dateRange: null, activeStatus: [] };
      localStorage.removeItem('deviceListFilters');
    },
    handleFilterRemove(filterId, filterValue) {
      if (Array.isArray(this.activeFilters[filterId])) {
        this.activeFilters[filterId] = this.activeFilters[filterId].filter((v) => v !== filterValue);
      } else {
        this.activeFilters[filterId] = null;
      }
    },
        openFileManager(row) {
      this.fileManagerDevice = { ...row };
      this.isFileManagerVisible = true;
    },
    // Device actions
   async loadDevice() {
  this.loading = true;
  try {
    await this.$store.dispatch(Action.device.load, { limit: 0, offset: 0 });
    // Auto-cancel stale provisions after loading
    this.devices.forEach(device => {
      if (device.current_provision) {
        const startedAt = new Date(device.current_provision.started_at);
        const minutesOld = (Date.now() - startedAt) / 1000 / 60;
        if (minutesOld > 15) {
          console.warn(`[DeviceList] Stale provision detected for ${device.name}, auto-cancelling`);
          this.$http.post(
            `/tenant/${this.activeEntity}/provision/${device.name}/cancel`
          ).catch(() => {}); // fire and forget
        }
      }
    });
            this.$store.dispatch('schedule/loadSummary');

  } catch (error) {
    console.error("Failed to load devices:", error);
  } finally {
    this.loading = false;
  }
  this.$nextTick(() => this.restoreSelection());
},
    updatePersistentSelection() {
      this.persistentSelectedIds = new Set(this.selectedDevices.map((d) => d.name));
    },
    restoreSelection() {
      if (this.persistentSelectedIds.size === 0) return;
      this.$refs.devicesTable?.clearSelection();
      const toSelect = this.filteredDevices.filter((d) => this.persistentSelectedIds.has(d.name));
      toSelect.forEach((d) => this.$refs.devicesTable?.toggleRowSelection(d, true));
    },
    handleSelectionChange(selectedRows) {
      this.selectedDevices = selectedRows;
      this.updatePersistentSelection();
    },
    clearPersistentSelection() {
      this.persistentSelectedIds.clear();
      this.selectedDevices = [];
      this.$refs.devicesTable?.clearSelection();
    },
    selectAllVisible() {
      this.filteredDevices.forEach((d) => this.$refs.devicesTable?.toggleRowSelection(d, true));
    },
    // Navigation
    onNew(type) {
      this.$router.push({ name: type === "single" ? Routes.deviceNew : Routes.devicesNew });
    },
    onEdit(row) {
      this.$router.push({ name: Routes.deviceEdit, params: { name: row.name } });
    },
    onDelete(row) {
      confirmDelete().then((result) => {
        if (result.value) this.$store.dispatch(Action.device.delete, row);
      });
    },
    // MQTT commands
    async AllDeviceAction(type) {
      const payloads = {
        refresh: { devdata: { core: { state: null }, fw: { ver_str: null }, power: { gauge: null } } },
        vibrate: { devcfg: { vibr: { song: "V101" } } },
      };
      const payload = payloads[type];
      if (!payload) return;
      try {
        await this.$store.dispatch(Action.device.AllDeviceAction, { payload });
        this.$notify({ title: "Success", message: `MQTT command '${type}' sent to all devices.`, type: "success" });
      } catch (error) {
        this.$notify({ title: "Error", message: `Failed: ${error.message}`, type: "error" });
      }
    },
    async sendMqttGeneralCommand(row, type) {
      const payloads = {
        refresh: { devdata: { core: { state: null }, fw: { ver_str: null }, power: { gauge: null } } },
        vibrate: { devcfg: { vibr: { song: "V10" } } },
        reboot: { devwork: { actions: [{ a: "reboot" }] } },
         allocate:   { devwork: { actions: [{ a: "alloc_local" }] } },
    unallocate: { devwork: { actions: [{ a: "return_local" }] } },
      };
      const payload = payloads[type];
      if (!payload) return;
      try {
        const result = await this.$store.dispatch(Action.device.sendMqttGeneralCommand, { deviceName: row.name, payload });
        if (result?.status === "success") {
          this.$notify({ title: "Success", message: `Command '${type}' sent to ${row.name}.`, type: "success" });
        } else {
          throw new Error(result?.error || "Unknown error");
        }
      } catch (error) {
        this.$notify({ title: "Error", message: `Failed: ${error.message}`, type: "error" });
      }
    },
    async sendMqttDFU(row) {
      try {
        const result = await this.$store.dispatch(Action.device.sendMqttDFU, { deviceName: row.name });
        if (result?.status === "success") {
          this.$notify({ title: "Success", message: `DFU sent to ${row.name}.`, type: "success" });
        } else {
          throw new Error(result?.error || "Unknown error");
        }
      } catch (error) {
        this.$notify({ title: "Error", message: `Failed: ${error.message}`, type: "error" });
      }
    },
    openFirmwareModal(row) {
  this.firmwareModalDevice = { ...row };
  this.isFirmwareModalVisible = true;
},
closeFirmwareModal() {
 // this.firmwareModalDevice = null;
  this.isFirmwareModalVisible = false;
  // optionally refresh device list to show updated fw_version
  this.loadDevice();
},
    async MultiDeviceAction(deviceIds, type) {
      const payloads = {
        refresh: { devdata: { core: { state: null }, fw: { ver_str: null }, power: { gauge: null } } },
        vibrate: { devcfg: { vibr: { song: "V10" } } },
        reboot: { devwork: { actions: [{ a: "reboot" }] } },
      };
      const payload = payloads[type];
      if (!payload) return;
      try {
        const result = await this.$store.dispatch(Action.device.MultiDeviceAction, { devices: [...deviceIds], payload });
        if (result?.status === "success") {
          this.$notify({ title: "Success", message: `Command '${type}' sent to devices.`, type: "success" });
        } else {
          throw new Error(result?.error || "Unknown error");
        }
      } catch (error) {
        this.$notify({ title: "Error", message: `Failed: ${error.message}`, type: "error" });
      }
    },
    async sendMultiDeviceDFU(deviceIds) {
      try {
        const result = await this.$store.dispatch(Action.device.sendMultiDeviceDFU, { devices: [...deviceIds] });
        if (result?.status === "success") {
          this.$notify({ title: "Success", message: `DFU sent to devices.`, type: "success" });
        } else {
          throw new Error(result?.error || "Unknown error");
        }
      } catch (error) {
        this.$notify({ title: "Error", message: `Failed: ${error.message}`, type: "error" });
      }
    },
    async deleteAllDevices() {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This will delete ALL devices in the current entity. This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete all!",
      });
      if (result.isConfirmed) {
        try {
          await this.$store.dispatch(Action.device.deleteAll);
        } catch (error) {
          console.error("Failed to delete all devices:", error);
        }
      }
    },
    // Provisioning — two-step flow
    handleProvisionClick(row) {
     // console.log('[DeviceList] handleProvisionClick', row.name, 'current_provision:', !!row.current_provision);
      if (row.current_provision) {
        // Already provisioning — skip file select, open progress modal directly
        this.openProvisioningModal(row);
      } else {
        // Open file selection dialog first
        this.currentProvisioningDevice = { ...row };
        this.isFileSelectVisible = true;
      }
    },
    async onFilesSelected(selectedCommands) {
    //  console.log('[DeviceList] ✅ onFilesSelected called with', selectedCommands.length, 'commands');
      // Snapshot device NOW before anything changes
      const device = { ...this.currentProvisioningDevice };
      const commands = selectedCommands;
      // Don't touch any state here — close-modal will fire next and call closeFileSelectModal
      // We kick off the POST asynchronously after yielding
      this._pendingProvision = { device, commands };
    },
    closeFileSelectModal() {
    //  console.log('[DeviceList] closeFileSelectModal called');
      this.isFileSelectVisible = false;
      this.currentProvisioningDevice = null;
      // If we have a pending provision queued by onFilesSelected, kick it off now
      if (this._pendingProvision) {
        const { device, commands } = this._pendingProvision;
        this._pendingProvision = null;
        this._startProvisionPost(device, commands);
      }
    },
    async _startProvisionPost(device, commands) {
      try {
      //  console.log('[DeviceList] POSTing provision for', device.name, 'with', commands.length, 'files');
        await this.$http.post(
          `/tenant/${this.activeEntity}/provision/${device.name}`,
          { commands }
        );
       // console.log('[DeviceList] ✅ POST succeeded, opening ModalProvision');
        this.currentProvisioningDevice = device;
        this.isProvModalVisible = true;
      } catch (error) {
        console.error('Failed to start provisioning:', error);
        this.$notify({
          title: 'Error',
          message: `Provisioning failed: ${error.response?.data?.error || error.message}`,
          type: 'error',
        });
      }
    },
    async cancelProvisioning(deviceName) {
      try {
        await this.$store.dispatch(Action.device.cancelProvisionProcess, deviceName);
        // If modal is open for this device, close it
        if (this.currentProvisioningDevice?.name === deviceName) {
          this.closeProvisioningModal();
        }
        this.$notify({ title: "Provisioning Cancelled", message: `Provisioning for ${deviceName} cancelled.`, type: "info" });
      } catch (error) {
        console.error("Failed to cancel provisioning:", error);
        this.$notify({ title: "Error", message: `Cancel failed: ${error.message}`, type: "error" });
      }
    },
openProvisioningModal(row) {
  // Deep copy — freeze the device state at open time
  this.currentProvisioningDevice = { ...row };
  this.isProvModalVisible = true;
},
     handleVisibilityChange() {
    if (!document.hidden) {
      this.loadDevice(); // reload when tab becomes visible
    }
  },
    closeProvisioningModal() {
      this.currentProvisioningDevice = null;
      this.isProvModalVisible = false;
        this.loadDevice();  
    },
    deleteSelected() {
      confirmDelete().then((result) => {
        if (result.value) {
          this.selectedDevices.forEach((device) => this.$store.dispatch(Action.device.delete, device));
          this.selectedDevices = [];
        }
      });
    },
    isProvisionStale(row) {
    if (!row.current_provision) return false;
    const startedAt = row.current_provision.started_at;
    if (!startedAt) return false;
    const minutesAgo = (Date.now() - new Date(startedAt)) / 1000 / 60;
    return minutesAgo > 15; // consider stale after 15 minutes
  },
  },
  mounted() {
    this.loadDevice();
    this.refreshInterval = setInterval(this.loadDevice, 30000);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

  },
  beforeUnmount() {
    clearInterval(this.refreshInterval);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);

  },
};
</script>
<style>
/* Sidebar Base Styles */
.sidebar {
  width: 250px;
  background-color: #303b51;
  color: white;
  border-radius: 20px;
  padding: 20px;
  position: fixed;
  top: 100px;
  left: 0;
  height: 90%;
  overflow-y: auto;
  z-index: 1050; /* Higher than Bootstrap's z-index */
  box-shadow: 2px 0 8px rgba(0.3, 0, 0, 0.3);
  transform: translateX(-100%); /* Hidden by default */
  opacity: 0;
  transition: transform 0.5s ease, opacity 0.5s ease, height 0.3s ease; /* Smooth transition */
}

/* When Sidebar is Fully Visible */
.sidebar-visible {
  transform: translateX(0); /* Fully visible */
  opacity: 1;
}

/* When Sidebar is Partially Visible on Hover */
.sidebar.hovered:not(.sidebar-visible) {
  transform: translateX(-80%); /* Slide out partially (20% visible) */
  opacity: 1;
  background-color: #303b51;
  color: white;
  border-radius: 20px;
  padding: 20px;
  position: fixed;
  top: 100px;
  left: 0;
  height: 90%;
  overflow-y: auto;
}

/* Toggle Button Styles */
.sidebar-toggle-btn {
  background-color: #303b51; /* Toggle button background */
  color: white; /* Toggle button icon color */
  border: 1px solid white; /* White border */
  transition: transform 0.5s ease;
  position: fixed; /* Ensure proper positioning */
  left: -1150px;
  z-index: 1060; /* Above the sidebar */
}

.sidebar-visible .sidebar-toggle-btn i {
  transform: rotate(
    180deg
  ); /* Rotates the arrow icon when sidebar is visible */
}

.sidebar-toggle-btn:hover {
  background-color: transparent; /* Transparent background on hover */
  color: white; /* Maintain white icon color */
  border: 1px solid white; /* Maintain white border */
}

/* Remove Conflicting Transition Classes */
.slide-enter-active,
.slide-leave-active,
.slide-enter,
.slide-leave-to {
  /* Commenting out to avoid conflicts */
  /* transform: translateX(-100%);
  opacity: 0; */
}

/* Other Existing Styles */
span.allocated {
  color: #6dd400;
}
span.returned {
  color: #e02020;
}
.battery-cell {
  display: flex;
  align-items: center;
  gap: 5px; /* Adds a small space between the icon and the text */
}

.charging-icon {
  color: #f7d20b; /* A yellow color for the lightning bolt */
  font-size: 1.2em; /* Makes the icon slightly larger */
}

/* Blinking light animation */
@keyframes blink {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.status-light {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-left: 10px;
}

/* Solid green light for Working status */
.green-light {
  background-color: green;
  animation: none;
}

/* Blinking red light for Defect status */
.red-light {
  background-color: red;
  animation: blink 1s infinite;
}

/* Status text colors */
.working-status {
  color: green;
}

.defect-status {
  color: red;
}

.filter-card {
  background-color: #f8f9fa;
  border-radius: 10px;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.1);
}

/* Styling for the filter panel and toggle button */
.filter-toggle-btn {
  position: absolute; /* Positioning for flexibility */
  top: 20px; /* Position near the top */
  left: 1050px; /* Offset from the left */
  background-color: #303b51; /* Dark background for contrast */
  color: white; /* White text color */
  border: 2px solid white; /* White border */
  border-radius: 50px; /* Creates a pill shape; use 50% for a circle */
  padding: 10px 20px; /* Adjust for desired size */
  font-weight: bold; /* Make text stand out */
  cursor: pointer; /* Pointer cursor on hover */
  transform: translateX(-100%);
  transition: background-color 0.3s ease, transform 0.2s ease; /* Smooth transition */
}

.filter-toggle-btn:hover {
  background-color: white; /* Changes background on hover */
  color: #303b51; /* Dark text on hover */
  transform: scale(1.05); /* Slight enlarge effect */
}

.d-flex {
  display: flex;
  transition: margin-left 0.3s ease;
}

.justify-content-between {
  justify-content: space-between;
}

.align-items-center {
  align-items: center;
}

/* Matching Add Device button size */
.filter-section {
  flex: 1;
  min-width: 200px;
  margin-right: 20px;
}

.b-form-group {
  margin-bottom: 15px;
}

/* Improved padding for the card */
.b-card-body {
  padding: 20px;
}

/* Align the table and the filters nicely */
.table-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Ensure 'filter-tags' is properly styled and visible */
.filter-tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 10px;
  background-color: #f9f9f9; /* Optional: background for visibility */
  border-radius: 4px; /* Optional */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); /* Optional */
  position: relative;
  z-index: 1060; /* Ensure it's above the sidebar */
}

.delete-selected-btn {
  background-color: #ff5f5f;
  border-radius: 50%; /* Round shape */
  padding: 10px;
  color: white;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.delete-selected-btn i {
  font-size: 16px;
}

.delete-selected-btn:hover {
  background-color: #ff4242; /* Darker red on hover */
}

/* Optional: Set a fixed height for table content scrolling */
.table-container {
  position: relative;
  max-height: 900px; /* Adjust as needed */
  overflow-y: auto;
  margin-top: 20px;
}

/* Shift when sidebar is fully open on hover */

/* Full shift when sidebar is fully open */

.fixed-header {
  position: sticky;
  top: 0;
  background-color: #f5f7fa; /* Match your table header background */
  z-index: 3; /* Ensure it stays above other headers */
  box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4); /* Optional for better visibility */
}

/* Overlay Styles */
.overlay {
  transition: opacity 0.5s ease;
  opacity: 0; /* Initial state when sidebar is hidden */
}

.sidebar-visible .overlay {
  opacity: 1; /* Fade in when sidebar is visible */
} /* Spinner animation for "Waiting" */
.waiting-icon i {
  color: #007bff;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Pulse animation for "Updating" */
.updating-icon i {
  color: #007bff;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
}

/* Static checkmark for "Updated" */
.updated-icon i {
  color: #28a745;
}
/* Provision Button Status Indicator */
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  top: -2px;
  right: -2px;
  border: 2px solid white;
}

.status-dot-idle {
  background-color: #6c757d;
  display: none; /* Hide when idle */
}

.status-dot-loading {
  background-color: #007bff;
  animation: pulse-dot 1.5s infinite;
}

.status-dot-success {
  background-color: #28a745;
}

.status-dot-error {
  background-color: #dc3545;
  animation: blink-dot 1s infinite;
}
::v-deep .gray-row {
  background-color: #cc1515 !important;
  color: #999;
}

/* Animations for status dots */
@keyframes pulse-dot {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.3);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes blink-dot {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0.3;
  }
}

/* Enhanced provision button states */
.btn-primary.provision-loading {
  background-color: #0056b3;
  border-color: #004085;
  cursor: not-allowed;
}

.btn-primary.provision-completed {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-primary.provision-failed {
  background-color: #dc3545;
  border-color: #dc3545;
}

/* Loading spinner enhancement */
.fa-spinner.fa-spin {
  animation: fa-spin 1s infinite linear;
}

/* Provision button hover effects */
.btn-primary:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
  transition: all 0.2s ease;
}

/* Disabled button styling */
.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Status text colors */
.provision-status-text {
  font-size: 0.875rem;
  font-weight: 500;
}

.provision-status-loading {
  color: #007bff;
}

.provision-status-success {
  color: #28a745;
}

.provision-status-error {
  color: #dc3545;
}
.el-table .warning-row {
  background-color: rgba(206, 206, 206, 0.144) !important;
}
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: 
    max-height 0.5s ease-in-out,
    transform 0.3s ease-in-out,
    opacity 0.2s ease-in-out;
  overflow: hidden;
}

.slide-fade-enter-active {
  transition: 
    max-height 0.5s ease-in-out,
    transform 0.4s ease-in-out 0.1s,
    opacity 0.3s ease-in-out 0.1s;
  max-height: 500px;
  overflow: hidden;
}

.slide-fade-leave-active {
  transition: 
    max-height 0.5s ease-in-out,
    transform 0.4s ease-in-out 0.1s,
    opacity 0.2s ease-in-out;
  max-height: 500px;
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(100%); /* slide down instead of up */
  opacity: 0;
  max-height: 0;
}

.slide-fade-enter-to,
.slide-fade-leave {
  transform: translateY(0);
  opacity: 1;
  max-height: 500px;
}


/* Sticky table header */
.el-table {
  overflow: visible !important;
}
.el-table__header-wrapper {
  position: sticky;
  top: 70px;
  z-index: 50;
  background-color: #f5f7fa;
}
.el-table__header-wrapper th.el-table__cell {
  background-color: #f5f7fa;
}

/* Sticky table header */
.el-table {
  overflow: visible !important;
}
.el-table__header-wrapper {
  position: sticky;
  top: 70px;
  z-index: 50;
  background-color: #f5f7fa;
}
.el-table__header-wrapper th.el-table__cell {
  background-color: #f5f7fa;
}
</style>